import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
    FaBuilding, 
    FaUserTie, 
    FaUsers, 
    FaCalendarAlt, 
    FaBell, 
    FaChartLine, 
    FaClipboardList,
    FaUserPlus,
    FaEye,
    FaArrowRight,
    FaFileAlt,
    FaSpinner,
    FaExclamationTriangle,
    FaMoneyBillWave,
    FaCalendarCheck,
    FaPlane,
    FaDownload
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        totalEmployees: 0,
        totalDepartments: 0,
        totalHrManagers: 0,
        loggedInUser: null,
        companyName: null,
        recentActivities: [],
        reportsList: []
    });
    
    // Use base_url from package.json
    const baseUrl = 'https://api.burrayhr.com';

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }
                
                const response = await axios.get(`${baseUrl}/api/reports/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                console.log('Reports API response:', response);
                
                // Extract dashboard data from the reports response
                const data = response.data;
                
                // Initialize report list based on the response structure
                let reportsList = [];
                
                if (Array.isArray(data.reports)) {
                    reportsList = data.reports;
                } else if (Array.isArray(data)) {
                    reportsList = data;
                }
                
                // Extract metrics from the data
                const totalEmployees = data.employees?.length || data.total_employees || 0;
                const totalDepartments = data.departments?.length || data.total_departments || 0;
                const totalHrManagers = data.immediate_managers?.length || data.hr_managers_count || 0;
                const loggedInUser = data.logged_in_user;
                const companyName = data.company?.name || 'your company';

                
                // Extract activities if available
                const activities = data.activities || [];
                
                // Update the dashboard data state
                setDashboardData({
                    totalEmployees,
                    totalDepartments,
                    totalHrManagers,
                    loggedInUser,
                    companyName,
                    recentActivities: activities,
                    reportsList
                });
                console.log('Dashboard data:', dashboardData);
                
                setError(null);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setError(error.message || 'Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [baseUrl]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
        } catch (e) {
            return 'Invalid Date';
        }
    };

    const getActivityIcon = (type) => {
        switch(type) {
            case 'employee_added':
                return <FaUserPlus className="activity-icon employee" />;
            case 'hr_manager_added':
                return <FaUserTie className="activity-icon hr" />;
            case 'department_created':
                return <FaBuilding className="activity-icon department" />;
            case 'employee_updated':
                return <FaClipboardList className="activity-icon update" />;
            default:
                return <FaBell className="activity-icon" />;
        }
    };

    const getReportIcon = (type) => {
        if (!type) return <FaFileAlt className="report-icon" />;
        
        const reportType = typeof type === 'string' ? type.toLowerCase() : '';
        
        switch(reportType) {
            case 'employee':
            case 'employees':
                return <FaUsers className="report-icon employee" />;
            case 'payroll':
                return <FaMoneyBillWave className="report-icon payroll" />;
            case 'department':
            case 'departments':
                return <FaBuilding className="report-icon department" />;
            case 'attendance':
                return <FaCalendarCheck className="report-icon attendance" />;
            case 'leave':
                return <FaPlane className="report-icon leave" />;
            case 'performance':
                return <FaChartLine className="report-icon performance" />;
            default:
                return <FaFileAlt className="report-icon" />;
        }
    };

    // Safety function to display a limited number of reports
    const getLimitedReports = (count = 4) => {
        if (!Array.isArray(dashboardData.reportsList)) return [];
        return dashboardData.reportsList.slice(0, count);
    };

  return (
        <div className="app-container">
            <Sidebar />
            <div className="main-wrapper">
        <Topbar />
                <div className="content-wrapper">
            <div className="admin-dashboard-container">
                        <div className="dashboard-header">
                            <div className="welcome-section">
                                <h3 style={{color: 'white'}}>Welcome back, {dashboardData?.loggedInUser?.first_name} {dashboardData?.loggedInUser?.last_name}</h3>
                                <p style={{color: 'white'}}>Here's what's happening in {dashboardData?.companyName} today.</p>
                            </div>
                            <div className="date-section">
                                <FaCalendarAlt className="calendar-icon" />
                                <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-spinner">
                                <div className="spinner"></div>
                                <p>Loading dashboard data...</p>
                            </div>
                        ) : error ? (
                            <div className="error-message">
                                <FaExclamationTriangle className="error-icon" />
                                <p>{error}</p>
                                <button onClick={() => window.location.reload()} className="retry-button">
                                    Retry
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Dashboard Metrics */}
                                <div className="dashboard-metrics">
                                    <div className="metric-card">
                                        <div className="metric-icon hr-managers">
                                            <FaUserTie />
                                        </div>
                                        <div className="metric-content">
                                            <h3>HR Managers</h3>
                                            <p className="metric-value">{dashboardData.totalHrManagers}</p>
                                            <Link to="/admin-manager/employees" className="metric-link">
                                                View all <FaArrowRight className="arrow-icon" />
                                            </Link>
                                        </div>
                                    </div>
                                    
                                    <div className="metric-card">
                                        <div className="metric-icon employees">
                                            <FaUsers />
                                        </div>
                                        <div className="metric-content">
                                            <h3>Employees</h3>
                                            <p className="metric-value">{dashboardData.totalEmployees}</p>
                                            <Link to="/admin-manager/employees" className="metric-link">
                                                View all <FaArrowRight className="arrow-icon" />
                                            </Link>
                                        </div>
                                    </div>
                                    
                                    <div className="metric-card">
                                        <div className="metric-icon departments">
                                            <FaBuilding />
                                        </div>
                                        <div className="metric-content">
                                            <h3>Departments</h3>
                                            <p className="metric-value">{dashboardData.totalDepartments}</p>
                                            <Link to="/admin-manager/departments" className="metric-link">
                                                View all <FaArrowRight className="arrow-icon" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="quick-actions-section">
                                    <h2>Quick Actions</h2>
                                    <div className="quick-actions">
                                        <Link to="/admin-manager/add-hr-manager" className="action-card">
                                            <FaUserPlus className="action-icon" />
                                            <span>Add HR Manager</span>
                                        </Link>
                                        <Link to="/admin-manager/add-employee" className="action-card">
                                            <FaUserPlus className="action-icon" />
                                            <span>Add Employee</span>
                                        </Link>
                                        <Link to="/admin-manager/add-department" className="action-card">
                                            <FaBuilding className="action-icon" />
                                            <span>Add Department</span>
                                        </Link>
                                    </div>
                                </div>

                                {/* Recent Activities */}
                                {dashboardData.recentActivities.length > 0 && (
                                <div className="recent-activities-section">
                                    <div className="section-header">
                                        <h2>Recent Activities</h2>
                                        <Link to="/admin-manager/activities" className="view-all-link">
                                            View All <FaEye />
                                        </Link>
                                    </div>
                                    <div className="activities-list">
                                            {dashboardData.recentActivities
                                                .slice(0, 15)
                                                .map((activity, index) => (
                                                    <div key={activity.id || index} className="activity-item">
                                                        {getActivityIcon(activity.activity)}
                                                <div className="activity-content">
                                                            <p className="activity-message">{activity.description}</p>
                                                            <span className="activity-time">{formatDate(activity.created_at)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
